import {
    ReservationModel,
    GroupInfoModel,
    CustomerModel,
    CompanyModel,
    RoomInfoModel,
    ReservationGuestReferenceModel,
    ReservationInvoicedStatus,
    GroupInfoStatus,
    ReservationStatus,
    SelfServiceCheckInModel,
    SelfCheckInLogModel,
    UserInfo,
    MinibarLogWithIncludedModels,
    InvoiceModelWithInvoiceItems,
    AccommodationPricelistModel,
    RoomInfoEditModel,
} from '@common/modelDefinition';
import { startGlobalLoading, stopGlobalLoading } from '@components/loaders/globalLoading';
import { v4 as uuidv4 } from 'uuid';
import { loadUserCondoData } from './condoOwnerView/condoApi';
import {
    getReservation,
    getSelfServiceCheckInByResId,
    getGroupInfo,
    addGuestToRes,
    saveOrUpdateReservation,
    saveSelfCheckIn,
    saveGroupRes,
    getResGuests,
    getCustomer,
    getCompany,
    getRoom,
    removeGuestFromRes,
    getPosTerminalAndMinibarProformaInvoices,
    getMinibarConsumption,
    getSelfCheckInLogsForRes,
    sendSelfCheckInDataNotification,
    updateResGuestReference,
    updateReservationGuestTax,
    getUserInfo,
    getDoorAccessLogsForRes,
    _getAllReservationsFromTo,
    deleteRes,
    createMinibarProformaInvoice,
    updateCus,
    updateResGuests,
    deleteMinibarLog,
    updateInvoiceAndRecalculateStatus,
} from './api';

import { _selfServiceCheckInTemplate } from './utils';
//@ts-ignore
import { toast } from 'react-toastify';
import translate from '@data/translations';
import dayjs from 'dayjs';
import { reservationEnums } from '@data/reservationStatusEnums';
import { getRoomPricelist } from './reservations/AssignPriceList';
import useStore from '@data/state/zustand';

export interface Reservation extends ReservationModel {
    sessionReservationId: string;
    Customer: CustomerModel | null;
    Company: CompanyModel | null;
    RoomInfo: RoomInfoEditModel;
    VisaInfo?: ReservationGuestReferenceModel[];
    Guests?: CustomerModel[];
    //DODANO 7.11.2021
    MinibarLogs?: MinibarLogWithIncludedModels[]; //proširivamo model rezervacija - tako da se ne koristi useEffect
    SelfCheckInLogs?: SelfCheckInLogModel[]; //proširujemo model tako da nigdje ne koristiš useEffect, već naloadaj podatke prilikom loadanja rezervacije!!!
    SelfServiceCheckIn?: SelfServiceCheckInModel;
    Invoices?: InvoiceModelWithInvoiceItems[]; //ovo će se nekad morat tipizirati... ali neka sada stoji ovako kao any[] - predračuni s posterminala ili minibara
    Recepcionist?: UserInfo | null;
    GroupInfo?: GroupReservation;
    EditedByUser?: UserInfo | null;
}

export interface GroupReservation extends GroupInfoModel {
    Customer: CustomerModel | null;
    Company: CompanyModel | null;
    Reservations: ReservationModel[] | null;
}

export interface ReservationGuestsEndpoint {
    reservationGuestReferences: ReservationGuestReferenceModel[];
    customers: CustomerModel[];
}

export interface ReservationValidationResult {
    messages: string[];
    isValid: boolean;
}

export interface ValidationReport {
    overlaps: ReservationModel[];
    validationInfo: ReservationValidationResult;
    isFullyValid: boolean;
}

export interface ReservationHolder {
    customer: CustomerModel | null;
    company: CompanyModel | null;
}

export interface ValidationParams {
    checkInTimestamp: boolean;
    accommodationPriceListIdOrOpenPrice: boolean;
    roomInfoId: boolean;
    groupReservationHolder: boolean;
    reservationHolder: boolean;
}

const defaultReservationValidationParams: ValidationParams = {
    checkInTimestamp: true,
    accommodationPriceListIdOrOpenPrice: true,
    roomInfoId: true,
    groupReservationHolder: true,
    reservationHolder: true,
};

async function getReservationFromReservationModels(
    reservation: ReservationModel
): Promise<Reservation | ReservationModel> {
    if (reservation?.id) {
        const newReservation = { ...reservation, sessionReservationId: uuidv4() } as Reservation;

        // NAPOMENA: svi ovi pozivi su nezavisni - nijednom ne treba rezultat drugoga, svima treba
        // samo ono sto vec postoji na `reservation` (id/customerId/companyId/roomInfoId/...).
        // Prije su isli sekvencijalno (await jedan pa drugi) sto lokalno nije primjetno (svaki poziv
        // je jeftin), ali u produkciji pod opterecenjem 7-9 uzastopnih cekanja zna dati 2-4s umjesto
        // da se sve dogodi istovremeno i ceka se samo najsporiji poziv.
        const guestsPromise = (async () => {
            const ref = await getResGuests(reservation.id as number);
            const newResGuestRef: ReservationGuestReferenceModel[] = [];
            for (let i = 0; i < ref.reservationGuestReferences.length; i++) {
                const guestRef = { ...ref.reservationGuestReferences[i] };
                const customer = ref.customers.find((c: CustomerModel) => c.id === guestRef.customerId);
                if (guestRef.documentId === null || guestRef.documentType === null) {
                    if (guestRef.documentId === null && customer?.documentId) {
                        guestRef.documentId = customer.documentId;
                    }
                    if (guestRef.documentType === null && customer?.documentType) {
                        guestRef.documentType = customer.documentType;
                    }
                    const updatedRef = await updateResGuests(guestRef);
                    newResGuestRef.push(updatedRef);
                } else {
                    newResGuestRef.push(guestRef);
                }
            }
            return { visaInfo: newResGuestRef, guests: ref.customers };
        })();

        const minibarLogsPromise = getMinibarConsumption(reservation.id);
        const invoicesPromise = getPosTerminalAndMinibarProformaInvoices(reservation.id);
        const customerPromise = reservation.customerId ? getCustomer(reservation.customerId) : Promise.resolve(null);
        const companyPromise = reservation.companyId ? getCompany(reservation.companyId) : Promise.resolve(null);
        const roomPromise = getRoom(reservation.roomInfoId);
        const selfServiceCheckInPromise = reservation.enableSelfCheckIn
            ? getSelfServiceCheckInByResId(reservation.id)
            : Promise.resolve(_selfServiceCheckInTemplate);
        const selfCheckInLogsPromise = reservation.enableSelfCheckIn
            ? getSelfCheckInLogsForRes(reservation.id)
            : Promise.resolve([]);
        const receptionistPromise = reservation.receptionistUuid
            ? getUserInfo(reservation.receptionistUuid)
            : Promise.resolve(null);
        const editedByUserPromise = reservation.editedByUserUuid
            ? getUserInfo(reservation.editedByUserUuid)
            : Promise.resolve(null);

        const [
            guestsResult,
            minibarLogs,
            invoices,
            customer,
            company,
            room,
            selfServiceCheckIn,
            selfCheckInLogs,
            receptionist,
            editedByUser,
        ] = await Promise.all([
            guestsPromise,
            minibarLogsPromise,
            invoicesPromise,
            customerPromise,
            companyPromise,
            roomPromise,
            selfServiceCheckInPromise,
            selfCheckInLogsPromise,
            receptionistPromise,
            editedByUserPromise,
        ]);

        //add visa info and guestReferences to reservation
        newReservation.VisaInfo = guestsResult.visaInfo;
        newReservation.Guests = guestsResult.guests;
        newReservation.MinibarLogs = minibarLogs;
        newReservation.Invoices = invoices;
        if (reservation.customerId) {
            //load reservation holders (this should be optimised to load with visa info)
            newReservation.Customer = customer;
        }
        if (reservation.companyId) {
            //load reservation company holders
            newReservation.Company = company;
        }
        newReservation.RoomInfo = room;
        newReservation.SelfServiceCheckIn = selfServiceCheckIn;
        newReservation.SelfCheckInLogs = selfCheckInLogs;
        if (reservation.receptionistUuid) {
            newReservation.Recepcionist = receptionist;
        }
        if (reservation.editedByUserUuid) {
            newReservation.EditedByUser = editedByUser;
        }

        return newReservation;
    } else {
        return reservation;
    }
}

function validateReservation(reservation: ReservationModel, params: ValidationParams): ReservationValidationResult {
    const messages: string[] = [];
    let isValid = true;
    if (!reservation) {
        return { isValid: false, messages };
    }
    if (params.checkInTimestamp && reservation.checkInTimestamp > reservation.checkOutTimestamp) {
        isValid = false;
        messages.push(translate("Checkin date can't be greater than checkout date!"));
    }

    if (
        params.accommodationPriceListIdOrOpenPrice &&
        reservation.accommodationPriceListId === null &&
        reservation.openPrice === null &&
        reservation.statusEnum !== reservationEnums.canceled
    ) {
        isValid = false;
        messages.push(translate('Please, select a price list or apply custom price!'));
    }

    if (params.reservationHolder && reservation.customerId === null && reservation.companyId === null) {
        isValid = false;
        messages.push(translate('Please, select customer or company!'));
    }

    if (params.roomInfoId && !reservation.roomInfoId) {
        isValid = false;
        messages.push(translate('Please, select a room!'));
    }
    //zašto je se ovo zakomentiralo????? zato sto se ne moze ni spremit onda ako namjerno hoces staviti da je closed
    // if (reservation.statusEnum === ReservationStatus.closed) {
    //     isValid = false;
    //     messages.push('Reservation is closed!');
    // }

    if (params.groupReservationHolder && reservation.groupUuid) {
        const group = useStore.getState().state.reservationForm2.groupReservation;

        if (!group?.customerId && !group?.companyId) {
            isValid = false;
            messages.push(translate('Please select group reservation holder!'));
        }
    }

    return { messages, isValid };
}

function getOverlappingReservations(
    reservation: Reservation | ReservationModel,
    reservations: ReservationModel[]
): ReservationModel[] {
    const currentStart = dayjs(reservation.checkInTimestamp);
    const currentEnd = dayjs(reservation.checkOutTimestamp);

    const overlaps: ReservationModel[] = [];

    let roomOtherReservations = reservations.filter((r) => {
        return (
            (Number(r.statusEnum) === ReservationStatus.earlyBird ||
                Number(r.statusEnum) === ReservationStatus.confirmed ||
                Number(r.statusEnum) === ReservationStatus.checkedIn ||
                Number(r.statusEnum) === ReservationStatus.autoBlocked) &&
            r.roomInfoId === Number(reservation.roomInfoId)
        );
    });
    if (reservation.id) {
        //filter out currently editing reservations
        roomOtherReservations = roomOtherReservations.filter((r) => r.id !== reservation.id);
        //filter out all canceled reservation - because they are unrelevant
        roomOtherReservations = roomOtherReservations.filter(
            (r) => Number(r.statusEnum) !== ReservationStatus.canceled
        );
    }
    roomOtherReservations.forEach((r) => {
        const tempStart = dayjs(r.checkInTimestamp);
        const tempEnd = dayjs(r.checkOutTimestamp);

        // Provjera preklapanja intervala:
        // Dva intervala [A, B] i [C, D] se preklapaju ako:
        // A <= D && C <= B

        const isOverlapping = currentStart.isSameOrBefore(tempEnd) && tempStart.isSameOrBefore(currentEnd);

        if (isOverlapping) {
            overlaps.push(r);
        }
    });
    if (reservation.statusEnum === ReservationStatus.canceled) {
        return [];
    } else {
        return overlaps;
    }
}

function getReservationTemplate(
    roomInfoId: number,
    checkInTimestamp: number,
    checkOutTimestamp: number,
    adultsNumber: number
): Reservation | null {
    const roomInfoMaps = useStore.getState().map.RoomInfo();
    const result: RoomInfoEditModel | undefined = roomInfoMaps?.[roomInfoId];
    if (result) {
        return {
            sessionReservationId: uuidv4(),
            checkInTimestamp: checkInTimestamp,
            checkOutTimestamp: checkOutTimestamp,
            roomInfoId: roomInfoId,
            statusEnum: ReservationStatus.confirmed,
            invoicedStatus: ReservationInvoicedStatus.notPaid,
            adultsNumber,
            enableSelfCheckIn: false,
            childrenNumber: 0,
            tags: null,
            accommodationPriceListId: null,
            customerId: null,
            companyId: null,
            receptionistUuid: useStore.getState().authTokenInfo?.user.uuid,
            RoomInfo: result,
            Customer: null,
            Company: null,
            VisaInfo: [],
            Guests: [],
            SelfServiceCheckIn: _selfServiceCheckInTemplate,
            SelfCheckInLogs: [],
            MinibarLogs: [],
            Invoices: [],
            Recepcionist: null,
            EditedByUser: null,
            condoUserUuid: null,
            groupUuid: null,
            openPrice: null,
            canceledReservationInvoicingStatus: 0,
            board: useStore.getState().otherSettings?.defaultReservationBoard || 'nb',
            editedByUserUuid: null,
            reservationSource: null,
            channelName: null,
            onlineReceptionResId: null,
            travelPurposeCode: null
        };
    } else {
        return null;
    }
}

interface ReservationControllerInterface {
    getReservations(): Reservation[];
    setActiveReservationIndex(index: number): void; //change selected tabs
    setActiveReservationsModels(index: number): void; //change selected tabs
    getActiveReservation(): Reservation;
    getActiveReservationIndex(): number;
    getGroupReservation(): GroupReservation | null;
    updateGroupReservation(groupInfo: GroupReservation): void;

    loadReservation(reservationId: number): void;
    loadGroup(groupUuid: string, reservationId?: number): void;

    clearAll(): void; //call only on unmount of main form component for memory cleanup

    addReservation(
        clearPrevious: boolean,
        roomInfoId: number,
        checkInTimestamp: number,
        checkOutTimestamp: number,
        adultsNumber: number,
        accommodationPriceListId: number | null,
        board: string | null,
        customerId?: number | null
    ): void;

    getFullValidationReport(params: ValidationParams | null): ValidationReport;
    getReservationValidationReport(reservation: Reservation): ValidationReport;

    save(isGroupForm: boolean): Promise<string>;

    setGroupReservationHolder(holder: ReservationHolder): void;
    updateReservationGuestReference(updatedGuestReference: any): void; //OVO MI JE SUMNJIVO??? GDJE GA UPDEJTAŠ???? AHA - SVUGDJE! OK!!
    updateGuestTax(
        visaInfoId: number,
        field: 'fixedTaxId' | 'fixedTaxId2',
        value: number | null,
        sourceType: 'tax' | 'posItem' | null
    ): Promise<void>;

    //odnosi se od sada na aktivnu rezervaciju!!!!
    updateActiveReservation(updatedReservation: ReservationModel): void;
    updateActiveReservationRoomInfo(roomInfo: RoomInfoModel): void;
    setSelfServiceCheckIn(s: SelfServiceCheckInModel, enableSelfCheckIn: boolean | null): void;
    setReservationHolder(holder: ReservationHolder): void;
    sendCheckInDataNotification(): void;
    removeGuestFromReservation(resGuestRefId: number): void;
    addGuestToReservation(
    customer: CustomerModel,
    fixedTaxId: number | null,
    fixedTaxId2?: number | null,
    fixedTaxType?: 'tax' | 'posItem' | null,
    taxManuallySet?: boolean,
    insuranceManuallySet?: boolean,
    fixedTaxType2?: 'tax' | 'posItem' | null
): void;
    deleteMinibarDataLog(minibarLog: MinibarLogWithIncludedModels): void;
    removeFromGroupRes(): void;
    getCustomersWithInvalidVisaIds(): number[]; //za koju rezervaciju su invalidni ili je ovo za save - provjeriti ovaj dio!!!! Koliko vidim samo za provjeru jedne rezervacije - aktivne!!!

    //doublecheck
    getGrupReservationHolder(): ReservationHolder;
    setGroupName(value?: string): void;
    getDoorAccessLogsForReservation(reservationId: number): Promise<any[]>;
    getAllReservationsFromTo(from: any, to: any, page: number, rows: number): Promise<ReservationModel[]>;
    deleteReservation(reservationId: number): void;

    setCondoUserUuid(condoUserUuid: string | null): void;
    addExistingGroup(groupUuid: string): void;
}

class ReservationController implements ReservationControllerInterface {
    setActiveReservationIndex(index: number) {
        useStore.getState().setNestedProperty(['state', 'reservationForm2', 'activeReservationIndex'], index);
    }

    async setActiveReservationsModels(index: number) {
        const reservations = this.getReservations();
        const targetReservation = reservations[index];

        const updatedActiveReservation = await getReservationFromReservationModels(targetReservation);
        const updatedReservations = reservations.map((r: Reservation, key: number) => {
            if (key === index) {
                const newReservation = { ...r, ...updatedActiveReservation };
                return newReservation;
            } else {
                return r;
            }
        });

        useStore.getState().setNestedProperty(['state', 'reservationForm2', 'reservations'], updatedReservations);
    }

    getActiveReservation(): Reservation {
        const activeReservationIndex = useStore.getState().state.reservationForm2.activeReservationIndex;
        return this.getReservations()[activeReservationIndex];
    }

    getActiveReservationIndex(): number {
        const activeReservationIndex = useStore.getState().state.reservationForm2.activeReservationIndex;
        return activeReservationIndex;
    }

    getEditedGroupReservationIds(): number[] {
        const editedGroupReservationIds = useStore.getState().state.reservationForm2.editedGroupReservationIds;
        return editedGroupReservationIds;
    }

    setReservationHolder(holder: ReservationHolder) {
        const { customer, company } = holder;
        const target = { ...this.getActiveReservation() };
        target.Customer = customer ? customer : null;
        target.Company = company ? company : null;
        target.companyId = company ? (company as any).id : null;
        target.customerId = customer ? (customer as any).id : null;
        this.updateActiveReservation(target);
    }

    setGroupReservationHolder(holder: ReservationHolder) {
        const { customer, company } = holder;
        const group = this.getGroupReservation();
        if (group) {
            const gr = { ...group };
            gr.Customer = customer ? customer : null;
            gr.Company = company ? company : null;
            gr.companyId = company ? (company as any).id : null;
            gr.customerId = customer ? (customer as any).id : null;
            useStore.getState().setNestedProperty(['state', 'reservationForm2', 'groupReservation'], gr);
            if (gr.Customer || gr.Company) {
                this.setGroupName();
            }
            useStore.getState().setNestedProperty(['state', 'reservationForm2', 'isPending'], true);
        }
    }

    setGroupName(value?: string) {
        const group = this.getGroupReservation();
        if (group) {
            const gr = { ...group };
            if (!value) {
                gr.groupName = new Date().toISOString().substring(0, 7);
                if (gr.Customer) {
                    gr.groupName = `${gr.groupName} ${gr.Customer.firstName ? gr.Customer.firstName : ''} ${
                        gr.Customer.lastName ? gr.Customer.lastName : ''
                    }`;
                }
                if (gr.Company) {
                    gr.groupName = `${gr.groupName} (${gr.Company.name})`;
                }
            } else {
                gr.groupName = value;
            }
            useStore.getState().setNestedProperty(['state', 'reservationForm2', 'groupReservation'], gr);
            useStore.getState().setNestedProperty(['state', 'reservationForm2', 'isPending'], true);
        }
    }

    setNote(value?: string) {
        const group = this.getGroupReservation();
        if (group) {
            const gr = { ...group };
            gr.note = value;
            useStore.getState().setNestedProperty(['state', 'reservationForm2', 'groupReservation'], gr);
            useStore.getState().setNestedProperty(['state', 'reservationForm2', 'isPending'], true);
        }
    }

    getGrupReservationHolder() {
        const group = this.getGroupReservation();
        if (group) {
            return { customer: group.Customer, company: group.Company };
        } else {
            return { customer: null, company: null };
        }
    }

    getReservations(): Reservation[] {
        const reservationsState = useStore.getState().state.reservationForm2.reservations;
        const reservations = reservationsState ? [...reservationsState] : [];
        return reservations as Reservation[];
    }

    getGroupReservation(): GroupReservation | null {
        const groupReservationCoverState = useStore.getState().state.reservationForm2.groupReservation;
        const groupReservationCover = groupReservationCoverState ? { ...groupReservationCoverState } : null;
        if (groupReservationCover.uuid) {
            return groupReservationCover as GroupReservation;
        } else {
            return null;
        }
    }

    setSelfServiceCheckIn(s: SelfServiceCheckInModel, enableSelfCheckIn: boolean | null) {
        const activeReservation = { ...this.getActiveReservation() };
        activeReservation.enableSelfCheckIn =
            enableSelfCheckIn === null ? activeReservation.enableSelfCheckIn : enableSelfCheckIn;
        activeReservation.SelfServiceCheckIn = { ...s };
        this.updateActiveReservation(activeReservation);
    }

    updateGroupReservation(groupInfo: GroupReservation) {
        const groupUuid = useStore.getState().state.reservationForm2.groupReservation.uuid;
        if (groupUuid) {
            const target = this.getGroupReservation() as any;
            if (target) {
                //update only provided atributes
                Object.keys(groupInfo).forEach((attribute: string) => {
                    target[attribute] = (groupInfo as any)[attribute];
                });
                useStore.getState().setNestedProperty(['state', 'reservationForm2', 'isPending'], true);
                useStore.getState().setNestedProperty(['state', 'reservationForm2', 'groupReservation'], target);
            }
        } else {
            throw new Error(translate('Reservations group does not exists!'));
        }
    }

    async addReservation(
        clearPrevious: boolean,
        roomInfoId: number,
        checkInTimestamp: number,
        checkOutTimestamp: number,
        adultsNumber: number,
        accommodationPriceListId: number | null,
        board: string | null,
        customerId?: number
    ) {
        startGlobalLoading();

        const newReservation: Reservation | null = getReservationTemplate(
            roomInfoId,
            checkInTimestamp,
            checkOutTimestamp,
            adultsNumber
        );
        if (!newReservation) {
            return null;
        }
        if (customerId) {
            newReservation.customerId = customerId;
        }
        if (accommodationPriceListId) {
            newReservation.accommodationPriceListId = accommodationPriceListId;
        }
        if (board) {
            newReservation.board = board;
        }
        if (clearPrevious) {
            this.clearAll();
        }

        this.addUpdateReservation(newReservation);
        const reservations = this.getReservations();
        this.setActiveReservationIndex(reservations.length - 1);
        useStore.getState().setNestedProperty(['state', 'reservationForm2', 'isPending'], true);
        stopGlobalLoading();
    }

    async loadReservation(reservationId: number) {
        startGlobalLoading();
        this.clearAll();
        try {
            const reservation: ReservationModel = await getReservation(reservationId);
            if (reservation) {
                reservation.tags = null
                if (reservation.groupUuid || reservation.previousGroupUuid) {
                    if (reservation.previousGroupUuid) {
                        await this.loadGroup(reservation.previousGroupUuid, reservationId);
                    } else if (reservation.groupUuid) {
                        await this.loadGroup(reservation.groupUuid, reservationId);
                    }
                } else {
                    const reservations: ReservationModel[] = [reservation];

                    const updatedActiveReservation: ReservationModel | Reservation =
                        await getReservationFromReservationModels(reservations[0]);
                    const updatedReservations = reservations.map((r: ReservationModel, index: number) => {
                        if (index === 0) {
                            const newReservation: ReservationModel | Reservation = {
                                ...r,
                                ...updatedActiveReservation,
                            };
                            return newReservation;
                        } else {
                            return r;
                        }
                    });
                    useStore
                        .getState()
                        .setNestedProperty(['state', 'reservationForm2', 'reservations'], updatedReservations);
                }
            }
            useStore.getState().setNestedProperty(['state', 'reservationForm2', 'isPending'], false);
            stopGlobalLoading();
        } catch (err) {
            console.log('err', err);
        }
        stopGlobalLoading();
    }

    async loadGroup(groupUuid: string, reservationId?: number) {
        startGlobalLoading();
        try {
            const groupRes: GroupReservation = await getGroupInfo(groupUuid);
            if (groupRes?.Reservations) {
                this.clearAll();
                let resIndex: number = 0;
                const reservations: ReservationModel[] = groupRes.Reservations;
                if (reservations.length === 0) {
                    toast(translate('Group does not have any reservation.'), {
                        position: toast.POSITION.BOTTOM_RIGHT,
                        type: toast.TYPE.ERROR,
                    });
                }
                if (reservationId) {
                    resIndex = reservations.findIndex((r) => r.id === reservationId);
                }
                this.setActiveReservationIndex(resIndex);
                const targetReservation = reservations[resIndex];
                const updatedActiveReservation = await getReservationFromReservationModels(targetReservation);
                const updatedReservations = reservations.map((r: ReservationModel, index: number) => {
                    if (index === resIndex) {
                        const newReservation = { ...r, ...updatedActiveReservation };
                        return newReservation;
                    } else {
                        return r;
                    }
                });

                useStore
                    .getState()
                    .setNestedProperty(['state', 'reservationForm2', 'reservations'], updatedReservations);
                useStore.getState().setNestedProperty(['state', 'reservationForm2', 'groupReservation'], groupRes);
            }
        } catch (err) {
            console.log('err', err);
        }
        stopGlobalLoading();
    }

    //stavlja rezervaciju u aktivnu ili ako postoji aktivna, onda kreira grupu i stavlja je u grupu kao drugu rezervaciju
    //ova je namijenjena da bude protected - tj da se samo koristi unutar ove klase - nipošto izvan!!!
    protected addUpdateReservation(reservation: Reservation) {
        const reservations = this.getReservations().map((r: Reservation) => {
            return { ...r }; //break state reference
        });
        const target = reservations.find(
            (r: Reservation) => r.id === reservation.id && r.roomInfoId === reservation.roomInfoId
        );
        if (target) {
            //update only provided atributes
            Object.keys(reservation).forEach((attribute: string) => {
                (target as any)[attribute] = (reservation as any)[attribute];
            });
            useStore.getState().setNestedProperty(['state', 'reservationForm2', 'isPending'], true);
            useStore.getState().setNestedProperty(['state', 'reservationForm2', 'reservations'], reservations);
        } else {
            if (reservations.length === 0) {
                //new reservation
                reservations.push(reservation);
                useStore.getState().setNestedProperty(['state', 'reservationForm2', 'reservations'], reservations);
            } else {
                //ovo znači da je grupna rezervacija jer dodajemo još jednu na stog
                const groupUuid = useStore.getState().state.reservationForm2.groupReservation.uuid;

                if (groupUuid) {
                    reservation.groupUuid = groupUuid;
                } else {
                    const groupRes: GroupInfoModel = {
                        uuid: uuidv4(),
                        checkOutTimestamp: reservation.checkOutTimestamp,
                        checkInTimestamp: reservation.checkInTimestamp,
                        invoicedStatus: ReservationInvoicedStatus.notPaid,
                        customerId: this.getReservations()[0].customerId,
                        companyId: this.getReservations()[0].companyId,
                        status: GroupInfoStatus.active,
                        receptionistUuid: useStore.getState().authTokenInfo?.user?.uuid,
                        type: 'reservation',
                    };
                    useStore.getState().setNestedProperty(['state', 'reservationForm2', 'groupReservation'], groupRes);
                    this.setGroupName();
                    reservation.groupUuid = groupRes.uuid;
                }
                reservations.push(reservation);
                const _allOtherReservations = reservations;
                const _reservations: Reservation[] = [];
                for (let i = 0; i < _allOtherReservations.length; i++) {
                    const res: any = { ..._allOtherReservations[i] };
                    res.groupUuid = reservation.groupUuid;
                    res.isPending = true;
                    _reservations.push(res);
                }
                useStore.getState().setNestedProperty(['state', 'reservationForm2', 'reservations'], _reservations);
            }
        }
    }

    async createNewRes(room: RoomInfoModel, acp: any) {
        if (room.id) {
            const { checkInTimestamp, checkOutTimestamp } = this.getActiveReservation();
            let targetPriceList = null;
            if (room) {
                const targetPricelists: AccommodationPricelistModel[] = getRoomPricelist(room, acp);
                if (targetPricelists?.length === 1) {
                    targetPriceList = acp.find(
                        (a: AccommodationPricelistModel) => targetPricelists[0].id && a.id === targetPricelists[0].id
                    );
                }
            }
            const accommodationPriceListId = targetPriceList?.id ? targetPriceList.id : null;
            const board = targetPriceList?.defaultBoard ? targetPriceList.defaultBoard : (useStore.getState().otherSettings?.defaultReservationBoard || 'nb');
            await this.addReservation(
                false,
                room.id,
                checkInTimestamp,
                checkOutTimestamp,
                1,
                accommodationPriceListId,
                board
            );
        }
    }

    async removeFromGroupRes() {
        //  const { groupUuid } = this.getActiveReservation();
        const activeReservation = { ...this.getActiveReservation() };
        activeReservation.groupUuid = null;
        // if(activeReservation.statusEnum !== reservationEnums.canceled && activeReservation.statusEnum !== reservationEnums.autoBlocked){
        //     activeReservation.previousGroupUuid = groupUuid;
        // }
        this.updateActiveReservation(activeReservation);
    }

    getCustomersWithInvalidVisaIds() {
        const invalidIds: number[] = [];
        const reservationGuestReferences: ReservationGuestReferenceModel[] = [];
        const reservationGuests: CustomerModel[] = [];
        const requiredVisaMap = useStore.getState().model.RequiredVisaMap;
        const requiredVisaMapCCodes =
            requiredVisaMap && requiredVisaMap?.map((v: any) => v.countryCode).filter((v: any) => v !== null);

        const activeReservation = this.getActiveReservation();
        activeReservation?.VisaInfo?.forEach((grf: ReservationGuestReferenceModel) => {
            reservationGuestReferences.push(grf);
        });

        activeReservation?.Guests?.forEach((c: CustomerModel) => {
            reservationGuests.push(c);
        });

        for (const r of reservationGuestReferences) {
            if (
                r.documentValidityPeriod &&
                r.placeEntry &&
                r.visaEntryDate &&
                r.visaTypeAndNumber &&
                r.visaValidityPeriod &&
                r.documentId &&
                r.documentType
            ) {
                //do nothing....
            } else {
                const customer = reservationGuests.find((c) => c.id === r.customerId);
                const nationalityCode = customer && customer.nationality ? Number(customer.nationality) : null;
                if (requiredVisaMapCCodes.includes(nationalityCode)) {
                    if (customer?.id) {
                        invalidIds.push(customer.id);
                    }
                } else if (r.documentId === null || r.documentType === null) {
                    if (customer?.id) {
                        invalidIds.push(customer.id);
                    }
                }
            }
        }

        return invalidIds;
    }

    getReservationValidationReport(reservation: Reservation): ValidationReport {
        const reservations = useStore.getState().model.Reservation;
        const overlaps = getOverlappingReservations(reservation, reservations as ReservationModel[]);
        const validationInfo = validateReservation(reservation, defaultReservationValidationParams);
        const isFullyValid = overlaps.length === 0 && validationInfo.isValid;
        return { overlaps, validationInfo, isFullyValid };
    }

    getFullValidationReport(params: ValidationParams | null): ValidationReport {
        const allReservationsForOverlaps = useStore.getState().model.Reservation;
        const overlaps: ReservationModel[] = [];
        let _isValid = true;
        const _messages: string[] = [];

        this.getReservations().forEach((reservation) => {
            const ovlps = getOverlappingReservations(reservation, allReservationsForOverlaps as ReservationModel[]);
            const vInfo = validateReservation(reservation, params ? params : defaultReservationValidationParams);

            ovlps.forEach((o) => {
                overlaps.push(o);
            });

            const { isValid, messages } = vInfo;
            if (!isValid) {
                _isValid = false; //if any is invelid, then all are invalid!!!
            }

            messages.forEach((msg: string) => {
                if (!_messages.includes(msg)) {
                    _messages.push(msg);
                }
            });
        });

        const isFullyValid = overlaps.length === 0 && _isValid;
        const validationInfo = { isValid: _isValid, messages: _messages };
        return { overlaps, validationInfo, isFullyValid };
    }

async addGuestToReservation(
    customer: CustomerModel,
    fixedTaxId: number | null,
    fixedTaxId2?: number | null,
    fixedTaxType?: 'tax' | 'posItem' | null,
    taxManuallySet?: boolean,
    insuranceManuallySet?: boolean,
    fixedTaxType2?: 'tax' | 'posItem' | null
) {
    try {
        const target = { ...this.getActiveReservation() };
        if (target.id) {
            const customerId = customer.id;
            const alreadyAdded = target?.VisaInfo?.find((c) => Number(c.customerId) === Number(customerId));
            if (!alreadyAdded) {
                if (target?.statusEnum !== ReservationStatus.closed) {
                    const resRef = await addGuestToRes(
                        customerId,
                        target.id,
                        fixedTaxId,
                        fixedTaxId2,
                        fixedTaxType,
                        taxManuallySet,
                        insuranceManuallySet,
                        fixedTaxType2
                    );
                    target.VisaInfo = target.VisaInfo ? [...target.VisaInfo] : [];
                    target.VisaInfo.push({ ...resRef });
                    target.Guests = target.Guests ? [...target.Guests] : [];
                    target.Guests.push({ ...customer });
                    
                    // MAKNUTO:
                    // const adultsNumber: number = target && target.adultsNumber !== null && target.adultsNumber !== undefined ? target.adultsNumber : 1;
                    // target.adultsNumber = target && target.Guests && target.Guests.length >= adultsNumber ? target.Guests.length : adultsNumber;
                    
                    this.updateActiveReservation(target);
                } else {
                    throw new Error('Reservation closed!');
                }
            }
        }
    } catch (error: any) {
        toast(error.message, {
            position: toast.POSITION.TOP_RIGHT,
            type: toast.TYPE.ERROR,
        });
    }
}

    async removeGuestFromReservation(resGuestRefId: number) {
    try {
        const activeReservation = { ...this.getActiveReservation() };
        if (activeReservation?.statusEnum !== ReservationStatus.closed) {
            let customerDelete: number | null = null;
            
            activeReservation.VisaInfo = activeReservation.VisaInfo
                ? [...activeReservation.VisaInfo].filter((v) => {
                      if (v.id !== Number(resGuestRefId)) {
                          return true;
                      } else {
                          customerDelete = v.customerId;
                          return false;
                      }
                  })
                : [];
                
            activeReservation.Guests = activeReservation.Guests
                ? [...activeReservation.Guests].filter((g) => g.id !== customerDelete)
                : [];
            
            // MAKNUTO: activeReservation.adultsNumber = activeReservation.Guests.length;
            
            // Provjeri da li je obrisani gost holder
            const wasHolder = activeReservation.customerId === customerDelete;
            
            // Sinkroniziraj guestTaxConfig - postavi visaInfoId na null
            // Ako je bio holder, postavi isHolder: true da novi holder nađe taj slot
            if (activeReservation.guestTaxConfig) {
                const currentConfig = typeof activeReservation.guestTaxConfig === 'string'
                    ? JSON.parse(activeReservation.guestTaxConfig)
                    : activeReservation.guestTaxConfig;
                
                if (currentConfig) {
                    activeReservation.guestTaxConfig = {
                        adults: (currentConfig.adults || []).map((a: any) => {
                            if (a.visaInfoId === resGuestRefId) {
                                return {
                                    ...a,
                                    visaInfoId: null,
                                    isHolder: wasHolder ? true : (a.isHolder || false),
                                };
                            }
                            return a;
                        }),
                        children: (currentConfig.children || []).map((c: any) => ({
                            ...c,
                            visaInfoId: c.visaInfoId === resGuestRefId ? null : c.visaInfoId,
                        })),
                    };
                }
            }
            
            await removeGuestFromRes(resGuestRefId);
            this.updateActiveReservation(activeReservation);
        } else {
            throw new Error('Reservation closed!');
        }
    } catch (error: any) {
        toast(error.message, {
            position: toast.POSITION.TOP_RIGHT,
            type: toast.TYPE.ERROR,
        });
    }
}


    protected async saveSelfServiceCheckIn(reservation: Reservation) {
        if (reservation.enableSelfCheckIn && reservation.id) {
            const clonedStateSelfServiceCheckIn = { ...reservation.SelfServiceCheckIn } as any;
            delete clonedStateSelfServiceCheckIn.pinCode;
            const _selfCheckIn = await saveSelfCheckIn(clonedStateSelfServiceCheckIn, reservation.id);
            const clonedRes = { ...reservation };
            clonedRes.SelfServiceCheckIn = { ..._selfCheckIn };
            this.updateActiveReservation(clonedRes);
        }
    }

    setCondoUserUuid(condoUserUuid: string | null) {
        const activeReservation = { ...this.getActiveReservation() };
        activeReservation.condoUserUuid = condoUserUuid;
        this.updateActiveReservation(activeReservation);
    }

    async save(isGroupForm: boolean): Promise<string> {
        let redirectPath = '';
        try {
            const { isFullyValid } = this.getFullValidationReport(null);

            if (!isFullyValid) {
                throw new Error(
                    translate('Reservation is not valid! Please ensure validity before sending to the server!')
                );
            }

            let reloadToReservationId = null; //only for non group reservation, if reload is required!!!!
            const groupCover = this.getGroupReservation();
            const activeReservation = this.getActiveReservation();
            const editedGroupReservationIds = this.getEditedGroupReservationIds();

            if (groupCover) {
                //it is a group reservation
                //beware!!! in this step, you can have reservation in this form which are ejected from the group cover but still showin in the tabs...
                const activeGroupReservations = this.getReservations().filter((r) => {
                    return groupCover?.Reservations
                        ? r.groupUuid === groupCover.uuid && r.id && editedGroupReservationIds?.includes(r.id)
                        : r.groupUuid === groupCover.uuid;
                });
                const newGroupReservations = this.getReservations()
                    .filter((r) => {
                        return groupCover?.Reservations ? !r.id : null;
                    })
                    ?.filter((r) => r !== null);
                const ejectedReservations = this.getReservations().filter((r) => {
                    return r.groupUuid !== groupCover.uuid;
                });

                const newGroupCover = { ...groupCover };
                newGroupCover.type = 'reservation';
                for (const r of ejectedReservations) {
                    await saveOrUpdateReservation(r);
                }

                const allReservationsForSave = [...activeGroupReservations, ...newGroupReservations];
                await saveGroupRes(allReservationsForSave, newGroupCover);
            } else {
                //it is a single reservation
                const updatedReservation = await saveOrUpdateReservation(activeReservation);
                if (!activeReservation.id) {
                    reloadToReservationId = updatedReservation.id;
                }
            }

            //SAVE SELF CHECKIN IN DATA
            for (const r of this.getReservations()) {
                await this.saveSelfServiceCheckIn(r); //save any potential selfReservationInfo
            }

            //reload data and redirect router
            if (isGroupForm && groupCover) {
                // redirectPath = `/groupReservationForm?uuid=${groupCover.uuid}`;
                if (activeReservation.groupUuid) {
                    await this.loadGroup(groupCover.uuid, activeReservation.id);
                } else {
                    await this.loadGroup(groupCover.uuid);
                }
            } else if (reloadToReservationId) {
                if (activeReservation.condoUserUuid) {
                    // redirectPath = `/condoReservation/${activeReservation.RoomInfo.id}/${reloadToReservationId}/edit`;
                    await this.loadCondoReservation(reloadToReservationId);
                } else {
                    // redirectPath = `/reservations/${reloadToReservationId}/edit`;
                    await this.loadReservation(reloadToReservationId);
                }
            } else if (activeReservation.id) {
                if (!activeReservation.condoUserUuid) {
                    // kad removam rezervaciju iz grupne prikazat ce mi nju
                    // redirectPath = `/reservations/${activeReservation.id}/edit`;
                    await this.loadReservation(activeReservation.id);
                } else {
                    await this.loadCondoReservation(activeReservation.id);
                }
                //edit condo rezervacije ne treba redirectati nigdje
            }

            // const _previousRoomInfo = this.getPreviousRoomInfoReservation()
            // if (_previousRoomInfo) {
                // try {
                    // hardwareClearRoomCodes(_previousRoomInfo)
                // } catch (err) {
                    // console.log('err', err);
                // }
            // }
            // this.setPreviousRoomInfoBeforeSwitchingReservation(null);

            useStore.getState().setNestedProperty(['state', 'reservationForm2', 'isPending'], false);
            useStore.getState().setNestedProperty(['state', 'reservationForm2', 'editedGroupReservationIds'], []);
        } catch (err) {
            console.log(err);
        }
        return redirectPath;
    }

    async deleteMinibarDataLog(minibarLog: MinibarLogWithIncludedModels) {
        // if (minibarLog.InvoiceItem) {
        //     return null;
        // }
        const activeReservation = { ...this.getActiveReservation() };
        let findItemInLog: MinibarLogWithIncludedModels | undefined = activeReservation.MinibarLogs
            ? [...activeReservation.MinibarLogs].find((m) => m.id === minibarLog.id)
            : undefined;

        //ovo nevalja! Mora biti na backendu funkcija koja ovo automatizira na neki način - promjjeni type na starom i dodaj novi. To mora biti unutar transakcije!!!!
        if (activeReservation && findItemInLog && activeReservation.roomInfoId) {
            await deleteMinibarLog(findItemInLog);
            activeReservation.MinibarLogs = activeReservation.MinibarLogs
                ? [...activeReservation.MinibarLogs].filter((l) => l.id !== minibarLog.id)
                : [];
            this.updateActiveReservation(activeReservation);
            useStore.getState().setNestedProperty(['state', 'reservationForm2', 'isPending'], false);
        }
    }

    //tehnički - ok je da ovdje ostane jer je vezano za rezervaciju. Jedino što treba malo validacija ubaciti unutar funkcije....
    async sendCheckInDataNotification() {
        const activeReservation = this.getActiveReservation();
        if (activeReservation.id) {
            return await sendSelfCheckInDataNotification(activeReservation.id);
        }
    }

    async updateReservationGuestReference(updatedGuestReference: ReservationGuestReferenceModel) {
        const reservations = this.getReservations().map((r: Reservation) => {
            return { ...r }; //break state reference
        });
        const target = reservations.find(
            (r: ReservationModel) => r.id === updatedGuestReference.reservationId
        ) as Reservation;

        if (target?.statusEnum === ReservationStatus.closed) {
            throw new Error('Reservation closed!');
        } else {
            console.log('updatedGuestReference',updatedGuestReference)
            await updateResGuestReference(updatedGuestReference);

            target.VisaInfo = target.VisaInfo
                ? [...target.VisaInfo].map((v) => {
                      if (v.id === updatedGuestReference.id) {
                          return { ...updatedGuestReference };
                      } else {
                          return { ...v };
                      }
                  })
                : [];

            useStore.getState().setNestedProperty(['state', 'reservationForm2', 'isPending'], true);
            useStore.getState().setNestedProperty(['state', 'reservationForm2', 'reservations'], reservations);
            this.getCustomersWithInvalidVisaIds();
        }
    }

    async updateGuestTax(
        visaInfoId: number,
        field: 'fixedTaxId' | 'fixedTaxId2',
        value: number | null,
        sourceType: 'tax' | 'posItem' | null
    ) {
        const reservations = this.getReservations().map((r: Reservation) => {
            return { ...r }; //break state reference
        });
        const target = reservations.find((r: Reservation) =>
            r.VisaInfo?.some((v) => v.id === visaInfoId)
        ) as Reservation;

        if (target?.statusEnum === ReservationStatus.closed) {
            throw new Error('Reservation closed!');
        }

        const updatedGuestReference = await updateReservationGuestTax(visaInfoId, field, value, sourceType);

        if (target) {
            target.VisaInfo = target.VisaInfo
                ? [...target.VisaInfo].map((v) =>
                      v.id === updatedGuestReference.id ? { ...updatedGuestReference } : { ...v }
                  )
                : [];

            useStore.getState().setNestedProperty(['state', 'reservationForm2', 'isPending'], true);
            useStore.getState().setNestedProperty(['state', 'reservationForm2', 'reservations'], reservations);
        }
    }

    async getDoorAccessLogsForReservation(reservationId: number) {
        return await getDoorAccessLogsForRes(reservationId);
    }

    async getAllReservationsFromTo(from: any, to: any, page: number, rows: number) {
        return await _getAllReservationsFromTo(
            dayjs(from).startOf('day').valueOf(),
            dayjs(to).endOf('day').valueOf(),
            page,
            rows
        );
    }

    async deleteReservation(reservationId: number) {
        await deleteRes(reservationId);
    }

    async updateCustomer(customer: CustomerModel) {
        await updateCus(customer);
    }

    async loadCondoReservation(reservationId: number) {
        startGlobalLoading();
        try {
            //try to find reservation in state - condo user dont have permissions to read public reservation info!!!!
            await loadUserCondoData();
            const condoReservation = useStore.getState().getItem(['model', 'Reservation'], 'id', reservationId);
            if (condoReservation) {
                useStore
                    .getState()
                    .setNestedProperty(['state', 'reservationForm2', 'reservations'], [condoReservation]);
            }

            useStore.getState().setNestedProperty(['state', 'reservationForm2', 'isPending'], false);
        } catch (err) {
            console.log('err', err);
        }
        stopGlobalLoading();
    }

    updateActiveReservationRoomInfo(roomInfo: RoomInfoEditModel) {
        if (roomInfo.id) {
            const activeReservation = { ...this.getActiveReservation() };
            // this.setPreviousRoomInfoBeforeSwitchingReservation(activeReservation.RoomInfo)
            activeReservation.RoomInfo = { ...roomInfo };
            activeReservation.roomInfoId = roomInfo.id;
            this.updateActiveReservation(activeReservation);
        }
    }

    // setPreviousRoomInfoBeforeSwitchingReservation(roomInfo: RoomInfoEditModel | null){
    //     useStore.getState().setNestedProperty(['state', 'reservationForm2', 'previousRoomInfo'], roomInfo);
    // }

    // getPreviousRoomInfoReservation(){
    //     return useStore.getState().state.reservationForm2.previousRoomInfo;
    // }


    clearAll() {
        useStore.getState().setNestedProperty(['state', 'reservationForm2', 'groupReservation'], {});
        useStore.getState().setNestedProperty(['state', 'reservationForm2', 'reservations'], []);
        useStore.getState().setNestedProperty(['state', 'reservationForm2', 'activeReservationIndex'], 0);
        useStore.getState().setNestedProperty(['state', 'reservationForm2', 'isPending'], false);
        // useStore.getState().setNestedProperty(['state', 'reservationForm2', 'previousRoomInfo'], null);
    }

    updateActiveReservation(updatedReservation: ReservationModel, condoCheckOut?: boolean, allowPriceEditWhenClosed?: boolean) {
        if (updatedReservation?.statusEnum === ReservationStatus.closed && !condoCheckOut && !allowPriceEditWhenClosed) {
            throw new Error('Reservation is closed!');
        } else {
            const _editedResIds: number[] = this.getEditedGroupReservationIds()
                ? [...this.getEditedGroupReservationIds()]
                : [];
            const activeReservationIndex = this.getActiveReservationIndex();
            const updatedReservations = this.getReservations().map((r: Reservation, index: number) => {
                if (index === Number(activeReservationIndex)) {
                    if (r?.id && _editedResIds?.includes(r?.id)) {
                    } else if (r.id) {
                        _editedResIds.push(r.id);
                    }
                    return { ...updatedReservation };
                } else {
                    return r;
                }
            });

            useStore.getState().setNestedProperty(['state', 'reservationForm2', 'isPending'], true);
            useStore.getState().setNestedProperty(['state', 'reservationForm2', 'reservations'], updatedReservations);
            useStore
                .getState()
                .setNestedProperty(['state', 'reservationForm2', 'editedGroupReservationIds'], _editedResIds);
        }
    }

    async createMinibarProformaInvoice(reservationId: number) {
        const activeReservation = { ...this.getActiveReservation() };
        if (activeReservation?.statusEnum === ReservationStatus.closed) {
            throw new Error('Reservation is closed!');
        } else {
            await createMinibarProformaInvoice(reservationId);
            activeReservation.MinibarLogs = await getMinibarConsumption(reservationId);
            activeReservation.Invoices = await getPosTerminalAndMinibarProformaInvoices(reservationId);
            this.updateActiveReservation(activeReservation);
            useStore.getState().setNestedProperty(['state', 'reservationForm2', 'isPending'], false);
        }
    }

    async addOrRemoveInvoice(invoice: InvoiceModelWithInvoiceItems, add = true) {
        await updateInvoiceAndRecalculateStatus(invoice, add);

        const activeReservation = { ...this.getActiveReservation() };
        let invoices: InvoiceModelWithInvoiceItems[] = activeReservation.Invoices
            ? [...activeReservation.Invoices]
            : [];
        if (add) {
            invoices.push(invoice);
        } else {
            invoices = invoices.filter((i: InvoiceModelWithInvoiceItems) => i.id !== invoice.id);
        }

        activeReservation.Invoices = invoices;
        this.updateActiveReservation(activeReservation);
        useStore.getState().setNestedProperty(['state', 'reservationForm2', 'isPending'], false);
    }
    async addExistingGroup(groupUuid: string) {
        const grup = await getGroupInfo(groupUuid);
        useStore.getState().setNestedProperty(['state', 'reservationForm2', 'groupReservation'], grup);
        const activeReservation = { ...this.getActiveReservation() };
        activeReservation.groupUuid = groupUuid;
        this.updateActiveReservation(activeReservation);
    }

    async loadSingleReservation(reservationId: number) {
        try {
            const reservation: ReservationModel = await getReservation(reservationId);
            if (reservation) {
                const resWithModels: ReservationModel | Reservation = await getReservationFromReservationModels(
                    reservation
                );
                return resWithModels;
            } else {
                return null;
            }
        } catch (err) {
            console.log('err', err);
        }
    }
}

export { getOverlappingReservations };
export { ReservationController };
